/**
 * Click-and-drag horizontal scroll for any element with `data-spv-drag-scroll`.
 *
 * Touch devices (`pointer: coarse`) already have native momentum scroll, so
 * we only activate on fine pointers (mouse/trackpad). During a drag we add
 * `is-dragging` to the element (CSS hooks `cursor: grabbing` + disables
 * `scroll-snap-type` so the rail doesn't fight the user); after a drag that
 * moved more than the threshold we swallow the next `click` on the element's
 * subtree so the user doesn't accidentally navigate / quick-add.
 *
 * Generic by design — applied initially to the cart recommendations rail
 * (`snippets/spv-cart-recommendations.liquid`) but reusable on any
 * overflow-x scroller.
 */
(function () {
  if (window.__spvDragScroll) return;
  window.__spvDragScroll = true;

  var DRAG_THRESHOLD_PX = 5;
  var fineMQ = window.matchMedia('(pointer: fine)');

  /** @type {Map<Element, { pointerId: number, startX: number, scrollStart: number, moved: boolean }>} */
  var active = new Map();

  function onPointerDown(event) {
    if (event.button !== 0) return; // primary mouse button only
    if (!fineMQ.matches) return; // touch handled natively
    var el = event.currentTarget;
    // Don't capture on form controls — let native interactions win.
    var tag = event.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    // Track the pointer but DO NOT call `setPointerCapture` yet — that would
    // retarget `pointerup` (and therefore the synthesized `click`) to the
    // rail, swallowing clicks on `<button>` / `<a>` inside the cards. We
    // only promote to capture after the user crosses the drag threshold.
    active.set(el, {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollStart: el.scrollLeft,
      moved: false,
    });
  }

  function onPointerMove(event) {
    var el = event.currentTarget;
    var state = active.get(el);
    if (!state || state.pointerId !== event.pointerId) return;
    var dx = event.clientX - state.startX;
    if (!state.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      state.moved = true;
      el.classList.add('is-dragging');
      // Now that we're definitely dragging, claim the pointer so we keep
      // receiving move/up even if it leaves the rail. This still retargets
      // the trailing click, but `endDrag` below swallows it on purpose.
      try {
        el.setPointerCapture(event.pointerId);
      } catch (_) {
        /* may throw if the pointer was released between events */
      }
    }
    if (state.moved) {
      el.scrollLeft = state.scrollStart - dx;
      event.preventDefault();
    }
  }

  function endDrag(event) {
    var el = event.currentTarget;
    var state = active.get(el);
    if (!state || state.pointerId !== event.pointerId) return;
    active.delete(el);
    try {
      el.releasePointerCapture(event.pointerId);
    } catch (_) {
      /* already released */
    }
    if (!state.moved) {
      el.classList.remove('is-dragging');
      return;
    }
    // Swallow the click that fires after pointerup on the same target chain.
    var swallow = function (ev) {
      ev.stopPropagation();
      ev.preventDefault();
      el.removeEventListener('click', swallow, true);
    };
    el.addEventListener('click', swallow, true);
    // Belt-and-suspenders: if no click fires, remove the listener after a tick.
    setTimeout(function () {
      el.removeEventListener('click', swallow, true);
      el.classList.remove('is-dragging');
    }, 0);
  }

  function bind(el) {
    if (el.__spvDragBound) return;
    el.__spvDragBound = true;
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    // Browsers natively drag <a> and <img> on mousedown — that fires
    // `dragstart` and hijacks our scroll-drag. Cancel it within the rail.
    el.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-spv-drag-scroll]');
    for (var i = 0; i < nodes.length; i++) bind(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  // Re-bind after cart-drawer morphs replace the rail node.
  document.addEventListener('cart:update', function () {
    // The morph is async — wait a tick so the new DOM is in place.
    setTimeout(function () {
      scan(document);
    }, 0);
  });
})();
